import { useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { CircleCheckIcon, CircleIcon, SendIcon, Undo2Icon } from "lucide-react";

import { RadioCard } from "simplydo/react/components/RadioCard/RadioCard";
import { RichText, RichTextViewer } from "simplydo/react/richtext";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
} from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";

import api from "api";
import util from "utils/utils";

import ConfigurableTable from "components/lib/ConfigurableTable";

import type {
  IdeaPreviewChallenge,
  IdeaPreviewIdea,
  IdeaWithAssessmentDetails,
} from "../types";

type SendAssessmentFeedbackModalProps = {
  idea: IdeaPreviewIdea & IdeaWithAssessmentDetails;
  challenge: IdeaPreviewChallenge;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
};

type AssessmentFeedbackTemplateField = {
  id: string;
  type: "assessment" | "assessmentFeedback";
  title?: string;
};

export const SendAssessmentFeedbackModal = ({
  idea,
  challenge,
  isOpen,
  setOpen,
}: SendAssessmentFeedbackModalProps) => {
  const { t } = useTranslation();
  const template = challenge?.ownerIdeaTemplate;
  const assessmentRounds = template?.assessments?.rounds || [];

  const existingRoundIds =
    idea?.assessmentFeedback?.map(
      (f: NonNullable<IdeaPreviewIdea["assessmentFeedback"]>[number]) =>
        f.assessmentRoundId,
    ) || [];

  const defaultRoundId =
    assessmentRounds.find(
      (r: { id?: string }) => r.id && !existingRoundIds.includes(r.id),
    )?.id || assessmentRounds.at(0)?.id;

  const [assessmentRoundId, setAssessmentRoundId] = useState(defaultRoundId);
  const [includeOverallScore, setIncludeOverallScore] = useState(true);
  const [fieldSelections, setFieldSelections] = useState<
    Record<string, boolean>
  >({});
  const selectedFieldIds = Object.entries(fieldSelections)
    .filter(([_, value]) => value)
    .map(([key]) => key);

  const [step, setStep] = useState<"configure" | "edit">("configure");
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [feedbackHtml, setFeedbackHtml] = useState("");

  const sentFeedback = idea?.assessmentFeedback?.find(
    (f: NonNullable<IdeaPreviewIdea["assessmentFeedback"]>[number]) =>
      f.assessmentRoundId === assessmentRoundId,
  );

  const sections =
    template?.body?.filter(
      (s: { type?: string; assessmentRoundId?: string }) =>
        s.type === "assessment" && s.assessmentRoundId === assessmentRoundId,
    ) || [];

  const roundFields: Array<{
    sectionName: string;
    field: AssessmentFeedbackTemplateField;
  }> = [];
  for (const section of sections) {
    for (const field of section.fields || []) {
      if (field.type !== "assessment" && field.type !== "assessmentFeedback") {
        continue;
      }
      roundFields.push({
        sectionName: section.name || "Untitled section",
        field: field as AssessmentFeedbackTemplateField,
      });
    }
  }

  const review = () => {
    if (!assessmentRoundId) return;
    api.ideas.previewAssessmentFeedback(
      idea._id,
      { assessmentRoundId, includeOverallScore, fieldSelections },
      ({ feedbackHtml }) => {
        const nextHtml = feedbackHtml || "";
        setGeneratedHtml(nextHtml);
        setFeedbackHtml(nextHtml);
        setStep("edit");
      },
      (e: { message: string }) => toast.error(e.message),
    );
  };

  const send = () => {
    if (!assessmentRoundId) return;
    api.ideas.sendAssessmentFeedback(
      idea._id,
      { assessmentRoundId, feedbackHtml },
      ({ saved }) => {
        const existing = idea.assessmentFeedback || [];
        const updated = [
          ...existing.filter(
            (e: NonNullable<IdeaPreviewIdea["assessmentFeedback"]>[number]) =>
              e.assessmentRoundId !== saved.assessmentRoundId,
          ),
          saved,
        ];
        util.onUpdateIdea(idea, { assessmentFeedback: updated });
        api.invalidateQueries(api.ideas.get, [idea._id]);
        toast.success("Assessment feedback sent");
        setOpen(false);
        setFeedbackHtml("");
        setGeneratedHtml("");
        setStep("configure");
        setAssessmentRoundId(defaultRoundId);
        setFieldSelections({});
        setIncludeOverallScore(true);
      },
      (e: { message: string }) => toast.error(e.message),
    );
  };

  const roundHasFeedback = (roundId: string) =>
    existingRoundIds.includes(roundId);

  return (
    <>
      <Modal open={isOpen} size="small">
        <ModalHeader>Send {t("generic.idea")} assessment feedback</ModalHeader>
        <ModalContent scrolling>
          {step === "configure" ? (
            <>
              <p>
                Use this window to send assessment feedback to the authors of
                this {t("generic.idea")}. Feedback is anonymised and only shown
                to the author of the {t("generic.idea")}.
              </p>

              <H4>Assessment round</H4>
              <RadioCard.Group
                onValueChange={(value) => {
                  setAssessmentRoundId(value);
                  setFieldSelections({});
                  setStep("configure");
                  setGeneratedHtml("");
                  setFeedbackHtml("");
                }}
                defaultValue={defaultRoundId ?? ""}
                editable
              >
                {assessmentRounds.map(
                  (round: { id: string; name?: string }) => (
                    <RadioCard.Item key={round.id} value={round.id}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          flexDirection: "column",
                        }}
                      >
                        <span>{round.name}</span>
                        <Label
                          size="small"
                          color={
                            roundHasFeedback(round.id) ? "green" : undefined
                          }
                        >
                          <Icon
                            icon={
                              roundHasFeedback(round.id)
                                ? CircleCheckIcon
                                : CircleIcon
                            }
                          />
                          {roundHasFeedback(round.id) ? "Sent" : "Pending"}
                        </Label>
                      </div>
                    </RadioCard.Item>
                  ),
                )}
              </RadioCard.Group>

              {sentFeedback ? (
                <div style={{ marginTop: 16 }}>
                  <Accordion>
                    <AccordionTitle index={0}>
                      View sent feedback
                    </AccordionTitle>
                    <AccordionContent index={0}>
                      <Card fluid>
                        <CardContent>
                          <RichTextViewer value={sentFeedback.feedback || ""} />
                        </CardContent>
                      </Card>
                    </AccordionContent>
                  </Accordion>
                </div>
              ) : null}

              <Checkbox
                toggle
                checked={includeOverallScore}
                onChange={(_e, d) => setIncludeOverallScore(d.checked)}
                style={{ marginTop: 24 }}
              >
                <CheckboxHeader>Include overall score</CheckboxHeader>
                <CheckboxContent>
                  Include the total weighted score from all assessors in the
                  feedback.
                </CheckboxContent>
              </Checkbox>

              <H4 style={{ marginTop: 16 }}>Fields</H4>
              <ConfigurableTable
                tableKey="assessmentFeedbackFields"
                data={roundFields}
                keyExtractor={(item) => item.field.id}
                selectedKeys={selectedFieldIds}
                onSelect={(item) => {
                  if (item === null) {
                    if (selectedFieldIds.length === 0) {
                      setFieldSelections(
                        roundFields.reduce(
                          (acc, item) => {
                            acc[item.field.id] = true;
                            return acc;
                          },
                          {} as Record<string, boolean>,
                        ),
                      );
                    } else {
                      setFieldSelections({});
                    }
                  } else {
                    setFieldSelections({
                      ...fieldSelections,
                      [item.field.id]: !fieldSelections[item.field.id],
                    });
                  }
                }}
                columns={[
                  {
                    key: "type",
                    name: "Type",
                    render: ({ item }) =>
                      item.field.type === "assessment" ? "Score" : "Feedback",
                  },
                  {
                    key: "sectionName",
                    name: "Section",
                    render: ({ item }) => item.sectionName,
                  },
                  {
                    key: "field",
                    name: "Field",
                    render: ({ item }) => item.field.title || "Untitled field",
                  },
                ]}
              />
            </>
          ) : (
            <>
              <p style={{ marginBottom: 12 }}>
                Review and edit the email content below before sending it to the{" "}
                {t("generic.idea")} author.
              </p>

              <FormField>
                <FormLabel>Feedback email content</FormLabel>
                <RichText
                  preset="simple"
                  value={feedbackHtml}
                  onChange={(html: string) => setFeedbackHtml(html)}
                />
              </FormField>
            </>
          )}
        </ModalContent>
        <ModalActions>
          {step === "configure" ? (
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={review} color="primary">
                Review
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setStep("configure");
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => {
                  util
                    .confirm(
                      "Reset feedback",
                      "Are you sure you want to reset the feedback to the previously generated version?",
                    )
                    .then(() => {
                      setFeedbackHtml(generatedHtml);
                    })
                    .catch(() => {});
                }}
              >
                <Icon icon={Undo2Icon} />
                Reset
              </Button>
              <Button onClick={send} disabled={!feedbackHtml} color="primary">
                <Icon icon={SendIcon} />
                Send
              </Button>
            </>
          )}
        </ModalActions>
      </Modal>
    </>
  );
};
